import { useEffect, useRef } from 'react';

import { Sentry } from '@/util/sentry';

type UseClosedTabAutoPaginationParams = {
  enabled: boolean;
  visibleCount: number;
  hasNextPage?: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => Promise<unknown>;
};

const MAX_AUTO_FETCH_ATTEMPTS = 5;

/**
 * Temporary workaround for closed inbox false empty states.
 *
 * Introduced on 2026-02-26 in commit bcb5fdc76d896656e60dbb1ca4f6879a2ba9c7a3
 * in PR https://github.com/primuminc/primum-app/pull/909.
 * Discovered on 2026-03-02 while smoke testing prod with Ask Anonymously user id 2.
 *
 * Cause:
 * Closed tab fetches mixed statuses, then filters on the client.
 * Authors should see only closed.
 * Recipient experts should see closed and inactive.
 *
 * User impact:
 * Some users looked fine if page 1 had matching rows.
 * Some users saw a false empty state if matches were only on later pages.
 *
 * This hook keeps fetching next pages while visible count is zero and more pages exist.
 *
 * TODO:
 * Remove after the API returns closed-tab rows scoped by user type: inquiry authors see closed only,
 * recipient experts see closed and inactive so the client can drop mixed fetch and client filtering.
 */
export function useClosedTabAutoPagination({
  enabled,
  visibleCount,
  hasNextPage,
  isLoading,
  isFetching,
  isFetchingNextPage,
  fetchNextPage,
}: UseClosedTabAutoPaginationParams) {
  const autoFetchAttemptsRef = useRef(0);

  useEffect(() => {
    if (!enabled) {
      autoFetchAttemptsRef.current = 0;

      return;
    }

    if (visibleCount > 0) {
      autoFetchAttemptsRef.current = 0;

      return;
    }

    if (!hasNextPage) {
      return;
    }

    if (isLoading || isFetching || isFetchingNextPage) {
      return;
    }

    if (autoFetchAttemptsRef.current >= MAX_AUTO_FETCH_ATTEMPTS) {
      return;
    }

    autoFetchAttemptsRef.current += 1;

    fetchNextPage().catch((error) => {
      Sentry.captureException(error, {
        tags: { scope: 'inbox', hook: 'useClosedTabAutoPagination' },
        extra: {
          hasNextPage,
          visibleCount,
          autoFetchAttempts: autoFetchAttemptsRef.current,
        },
      });
    });
  }, [
    enabled,
    visibleCount,
    hasNextPage,
    isLoading,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
  ]);
}
